import { BlurFilter, Filter, GlProgram, Texture, TexturePool, UniformGroup, defaultFilterVert } from 'pixi.js'

// Bloom (item H): the bright parts of what the filtered container draws,
// blurred and added back on top. Three passes per frame on the container's
// bounds only (the explosion and flash): bright-pass -> blur -> combine.
// WebGL only (the stage asks for WebGL).

const brightFrag = `
in vec2 vTextureCoord;
out vec4 finalColor;
uniform sampler2D uTexture;
uniform float uThreshold;
void main() {
    vec4 c = texture(uTexture, vTextureCoord);
    float l = dot(c.rgb, vec3(0.299, 0.587, 0.114));
    finalColor = c * smoothstep(uThreshold, uThreshold + 0.25, l);
}
`
const combineFrag = `
in vec2 vTextureCoord;
out vec4 finalColor;
uniform sampler2D uTexture;
uniform sampler2D uBloomTexture;
uniform float uStrength;
void main() {
    vec4 base = texture(uTexture, vTextureCoord);
    vec4 glow = texture(uBloomTexture, vTextureCoord);
    finalColor = base + glow * uStrength;
}
`

class BrightPass extends Filter {
    constructor(threshold) {
        super({
            glProgram: GlProgram.from({ vertex: defaultFilterVert, fragment: brightFrag, name: 'gate1-bloom-bright' }),
            resources: { brightUniforms: new UniformGroup({ uThreshold: { value: threshold, type: 'f32' } }) },
            resolution: 'inherit',
        })
    }
}

export class BloomFilter extends Filter {
    constructor({ threshold = 0.5, strength = 1, blur = 8, quality = 3 } = {}) {
        super({
            glProgram: GlProgram.from({ vertex: defaultFilterVert, fragment: combineFrag, name: 'gate1-bloom-combine' }),
            resources: {
                bloomUniforms: new UniformGroup({ uStrength: { value: strength, type: 'f32' } }),
                uBloomTexture: Texture.WHITE.source,
            },
            resolution: 'inherit',
        })
        this.bright = new BrightPass(threshold)
        this.blur = new BlurFilter({ strength: blur, quality, resolution: 'inherit' })
        this.padding = this.blur.padding
    }
    get strength() { return this.resources.bloomUniforms.uniforms.uStrength }
    set strength(v) { this.resources.bloomUniforms.uniforms.uStrength = v }

    apply(filterManager, input, output, clearMode) {
        const bright = TexturePool.getSameSizeTexture(input)
        this.bright.apply(filterManager, input, bright, true)
        const glow = TexturePool.getSameSizeTexture(input)
        this.blur.apply(filterManager, bright, glow, true)
        this.resources.uBloomTexture = glow.source
        filterManager.applyFilter(this, input, output, clearMode)
        TexturePool.returnTexture(glow)
        TexturePool.returnTexture(bright)
    }
}
